import React, { useEffect, useRef, useState } from "react";
import { Camera, X, Check, RefreshCw, Upload, Loader2 } from "lucide-react";

/**
 * Consistent, in-app camera capture. Flow: live camera → tap shutter → see the
 * photo you took → tap Done (or Retake). Never opens an external camera app.
 * Calls onCapture(blob, filename) then closes. Upload fallback only if the
 * camera can't start.
 */
export default function CameraCapture({ onCapture, onClose, busy = false, label = "Capture photo" }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [snapshot, setSnapshot] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        if (!active) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setReady(true);
      } catch {
        setError("Camera unavailable — use Upload instead.");
      }
    })();
    return () => {
      active = false;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const snap = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    setSnapshot(canvas.toDataURL("image/jpeg", 0.9));
  };

  const done = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (blob) onCapture(blob, `return-photo-${Date.now()}.jpg`);
      setSnapshot(null);
      onClose();
    }, "image/jpeg", 0.9);
  };

  const onFile = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => onCapture(f, f.name));
    if (fileRef.current) fileRef.current.value = "";
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col" data-testid="camera-capture">
      <div className="flex items-center justify-between px-6 py-4 text-white">
        <span className="text-lg font-semibold">{label}</span>
        <button onClick={onClose} data-testid="camera-close-btn" className="p-3 rounded-full hover:bg-white/10">
          <X className="w-7 h-7" />
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center overflow-hidden px-4">
        {error ? (
          <div className="text-center text-white/80 max-w-md">
            <p className="mb-6 text-lg">{error}</p>
            <button onClick={() => fileRef.current?.click()} data-testid="camera-fallback-upload"
              className="inline-flex items-center gap-3 bg-white text-slate-900 font-bold text-lg px-8 py-5 rounded-2xl">
              <Upload className="w-6 h-6" /> Upload photo
            </button>
          </div>
        ) : snapshot ? (
          <img src={snapshot} alt="preview" className="max-h-full max-w-full rounded-xl" data-testid="camera-preview" />
        ) : (
          <video ref={videoRef} playsInline muted className="max-h-full max-w-full rounded-xl bg-black" data-testid="camera-video" />
        )}
        <canvas ref={canvasRef} className="hidden" />
        <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="camera-file-input" />
      </div>

      <div className="px-6 py-8 flex items-center justify-center gap-6">
        {snapshot ? (
          <>
            <button onClick={() => setSnapshot(null)} data-testid="camera-retake-btn" className="inline-flex items-center gap-2 bg-white/15 text-white font-semibold text-lg px-8 py-5 rounded-2xl hover:bg-white/25">
              <RefreshCw className="w-6 h-6" /> Retake
            </button>
            <button onClick={done} disabled={busy} data-testid="camera-accept-btn" className="inline-flex items-center gap-2 bg-emerald-500 text-white font-bold text-lg px-12 py-5 rounded-2xl hover:bg-emerald-600 disabled:opacity-60">
              {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Check className="w-6 h-6" />} Done
            </button>
          </>
        ) : (
          <button
            onClick={snap}
            disabled={!ready}
            data-testid="camera-shutter-btn"
            className="w-24 h-24 rounded-full bg-white ring-4 ring-white/40 flex items-center justify-center disabled:opacity-40 active:scale-95 transition-transform"
            aria-label="Take photo"
          >
            <Camera className="w-10 h-10 text-slate-900" />
          </button>
        )}
      </div>
    </div>
  );
}
